import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, MessageCircle, Send, UserCheck } from "lucide-react";
import PageHeader from "shared/PageHeader";
import Alert from "shared/Alert";
import KpiCard from "shared/KpiCard";
import FilterChip from "shared/FilterChip";
import { FORM_CONTROL, TABLE_SHELL, TBODY_ROW, TD, TH, THEAD_ROW } from "shared/tableStyles";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "./apiError";
import { getOutreachStats, type OutreachStats, type ReplyStats, type StatsRow } from "./api/OutreachTouchesApi";
import { formatCalendarDate, karachiToday } from "./outreachDates";
import LinkedInHubTabs from "./ui/LinkedInHubTabs";
import Delta, { deltaInfo } from "./ui/Delta";
import ColumnChart from "./ui/charts/ColumnChart";
import SendTimeGrid, { WEEKDAYS } from "./ui/charts/SendTimeGrid";
import LineChart from "./ui/charts/LineChart";
import { ChartCard, SERIES_COLORS } from "./ui/charts/chartKit";

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** The quick ranges, all ending today (Karachi). */
function presets(today: string): { key: string; label: string; from: string; to: string }[] {
  return [
    { key: "7", label: "Last 7 days", from: addDays(today, -6), to: today },
    { key: "30", label: "Last 30 days", from: addDays(today, -29), to: today },
    { key: "90", label: "Last 90 days", from: addDays(today, -89), to: today },
    { key: "month", label: "This month", from: `${today.slice(0, 8)}01`, to: today },
  ];
}

export function formatHours(h: number | null): string {
  if (h === null) return "—";
  if (h < 48) return `${Math.round(h)} h`;
  return `${(h / 24).toFixed(1).replace(/\.0$/, "")} days`;
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDay = (iso: string) => `${Number(iso.slice(8, 10))} ${MONTHS[Number(iso.slice(5, 7)) - 1]}`;
const OUTCOME_ROWS: { key: keyof ReplyStats["outcomes"]; label: string }[] = [
  { key: "interested", label: "Interested" }, { key: "teaming", label: "Teaming" }, { key: "other", label: "Other" },
  { key: "redirected", label: "Redirected to a colleague" }, { key: "not_interested", label: "Not interested" }, { key: "none", label: "No call recorded yet" },
];
const SHORT_BUCKET: Record<string, string> = { "Under 1 day": "< 1 d", "1 to 2 days": "1-2 d", "2 to 3 days": "2-3 d", "3 to 5 days": "3-5 d", "5 to 7 days": "5-7 d", "7 days or more": "7+ d" };
const pct = (v: number | null) => (v === null ? "—" : `${v}%`);

const FUNNEL: { key: keyof OutreachStats["funnel"]; label: string; bar: string }[] = [
  { key: "requests_sent", label: "Requests sent", bar: "bg-primary" },
  { key: "accepted", label: "Accepted", bar: "bg-teal" },
  { key: "message_sent", label: "Message sent", bar: "bg-info" },
  { key: "follow_up_sent", label: "Follow-up sent", bar: "bg-magenta" },
  { key: "replied", label: "Replied", bar: "bg-success" },
  { key: "positive", label: "Interested or teaming", bar: "bg-success" },
];

/** Bars for a few labelled counts against a total (one hue, value and share at the end of each row). */
function BarList({ rows, total, color, testId }: { rows: { label: string; count: number }[]; total: number; color: string; testId: string }) {
  return (
    <div className="space-y-2.5">
      {rows.map((r, i) => (
        <div key={r.label} className="grid grid-cols-[11rem_1fr_5rem] items-center gap-3 text-sm" data-testid={`${testId}-${i}`}>
          <span className="truncate text-foreground">{r.label}</span>
          <div className="h-3 rounded-full bg-surface-muted overflow-hidden" aria-hidden>
            <div className="h-full rounded-full" style={{ width: `${total ? Math.max(r.count > 0 ? 2 : 0, Math.round((r.count / total) * 100)) : 0}%`, background: color }} />
          </div>
          <span className="text-right tabular-nums"><strong className="font-semibold">{r.count}</strong> <span className="text-xs text-muted-foreground">{total ? Math.round((r.count / total) * 100) : 0}%</span></span>
        </div>
      ))}
    </div>
  );
}

const FEW = 5;        // a group with fewer requests than this says little: it is shown muted and tagged
const MAX_STATES = 8;

/** The biggest states one by one and the rest summed into "Other states", with the rates recomputed from the summed counts. */
export function foldStates(rows: (StatsRow & { label: string })[]): (StatsRow & { label: string })[] {
  const real = rows.filter((r) => r.label !== "State unknown" && r.label !== "Outside US");
  const rest = rows.filter((r) => r.label === "State unknown" || r.label === "Outside US");
  const top = real.slice(0, MAX_STATES);
  const tail = [...real.slice(MAX_STATES)];
  const sum = (k: keyof StatsRow) => tail.reduce((a, r) => a + (r[k] as number), 0);
  const other = tail.length ? [{
    label: `Other states (${tail.length})`, requests_sent: sum("requests_sent"), accepted: sum("accepted"), acceptance_matured: sum("acceptance_matured"),
    acceptance_matured_accepted: sum("acceptance_matured_accepted"), replied: sum("replied"),
    acceptance_rate: sum("acceptance_matured") ? Math.round((sum("acceptance_matured_accepted") / sum("acceptance_matured")) * 1000) / 10 : null,
    reply_rate: sum("requests_sent") ? Math.round((sum("replied") / sum("requests_sent")) * 1000) / 10 : null,
  }] : [];
  return [...top, ...other, ...rest];
}

function Breakdown({ title, rows, label, first }: { title: string; rows: (StatsRow & { id: string; name: string })[]; label: string; first?: string }) {
  if (rows.length === 0) return null;
  return (
    <section aria-label={title} className="space-y-2">
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      <div className={`${TABLE_SHELL} overflow-hidden`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={THEAD_ROW}>
              <th className={TH}>{label}</th>
              <th className={`${TH} text-right`}>Requests</th>
              <th className={`${TH} text-right`}>Accepted</th>
              <th className={`${TH} text-right`}>Acceptance</th>
              <th className={`${TH} text-right`}>Replied</th>
              <th className={`${TH} text-right`}>Reply rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={`${TBODY_ROW} ${r.requests_sent < FEW ? "text-muted-foreground" : ""}`}>
                <td className={`${TD} max-w-[22rem] truncate`} title={r.name}>{r.name}{r.requests_sent < FEW && <span className="ml-2 text-xs">few requests</span>}</td>
                <td className={`${TD} text-right tabular-nums`}>{r.requests_sent}</td>
                <td className={`${TD} text-right tabular-nums`}>{r.accepted}</td>
                <td className={`${TD} text-right tabular-nums`} title={r.acceptance_matured ? `${r.acceptance_matured_accepted} of ${r.acceptance_matured} sent 7+ days ago` : "Nothing sent 7+ days ago yet"}>{pct(r.acceptance_rate)}</td>
                <td className={`${TD} text-right tabular-nums`}>{r.replied}</td>
                <td className={`${TD} text-right tabular-nums`}>{pct(r.reply_rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {first && <p className="text-xs text-muted-foreground">{first}</p>}
    </section>
  );
}

/** LinkedIn Performance: the funnel and rates of the requests sent in a date range. */
export default function LinkedInPerformancePage() {
  const [params, setParams] = useSearchParams();
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const [data, setData] = useState<OutreachStats | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const latest = useRef(0);
  const today = karachiToday();
  const ranges = presets(today);

  const setRange = (f: string, t: string) => setParams((prev) => {
    const p = new URLSearchParams(prev);
    if (f) p.set("from", f); else p.delete("from");
    if (t) p.set("to", t); else p.delete("to");
    return p;
  }, { replace: true });

  useEffect(() => {
    const mine = ++latest.current;      // a slower, older answer must never overwrite a newer one
    setLoading(true);
    getOutreachStats({ fromDate: from || undefined, toDate: to || undefined })
      .then((d) => { if (mine === latest.current) { setData(d); setError(""); } })
      .catch((e) => { if (mine === latest.current) setError(apiErrorMessage(e, "Could not load the performance numbers.")); })
      .finally(() => { if (mine === latest.current) setLoading(false); });
  }, [from, to]);

  const shownFrom = data?.from_date ?? from;
  const shownTo = data?.to_date ?? to;
  const activePreset = ranges.find((r) => r.from === shownFrom && r.to === shownTo)?.key ?? (!from && !to ? "30" : "");
  const f = data?.funnel;
  const sent = f?.requests_sent ?? 0;
  const span = data ? Math.round((Date.parse(data.to_date) - Date.parse(data.from_date)) / 86400000) + 1 : 0;
  const before = `the previous ${span} ${span === 1 ? "day" : "days"}`;
  const weeks = (data?.by_week ?? []).map((w) => shortDay(w.week_start));
  const activitySeries = [
    { key: "sent", label: "Requests sent", color: SERIES_COLORS[0] },
    { key: "accepted", label: "Accepted", color: SERIES_COLORS[1] },
    { key: "replied", label: "Replied", color: SERIES_COLORS[2] },
  ];
  const rateSeries = [
    { key: "acceptance", label: "Acceptance rate", color: SERIES_COLORS[1] },
    { key: "reply", label: "Reply rate", color: SERIES_COLORS[2] },
  ];

  return (
    <div className="space-y-6">
      <div><LinkedInHubTabs /></div>
      <PageHeader
        title="LinkedIn Outreach"
        description={data ? `Performance of the requests sent ${formatCalendarDate(data.from_date, false)} to ${formatCalendarDate(data.to_date, false)} (Karachi)` : "Loading…"}
      />
      {error && <Alert tone="danger" role="alert">{error}</Alert>}

      <div className="flex flex-wrap items-center gap-2">
        {ranges.map((r) => (
          <FilterChip key={r.key} active={activePreset === r.key} onClick={() => setRange(r.key === "30" ? "" : r.from, r.key === "30" ? "" : r.to)}>{r.label}</FilterChip>
        ))}
        <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground ml-1">
          From
          <input type="date" value={shownFrom} max={shownTo || undefined} onChange={(e) => e.target.value && setRange(e.target.value, shownTo)} className={`px-2 py-1.5 ${FORM_CONTROL}`} aria-label="Range start" />
        </label>
        <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          to
          <input type="date" value={shownTo} min={shownFrom || undefined} max={today} onChange={(e) => e.target.value && setRange(shownFrom, e.target.value)} className={`px-2 py-1.5 ${FORM_CONTROL}`} aria-label="Range end" />
        </label>
      </div>

      {!data && !error && <p className="text-sm text-muted-foreground">Loading…</p>}
      {data && f && (
        <div className={`space-y-6 ${loading ? "opacity-60 transition-opacity" : ""}`} aria-busy={loading}>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" role="group" aria-label="Performance at a glance">
            <KpiCard icon={Send} label="Requests sent" value={f.requests_sent} tone="brand"
              hint={<>{f.requests_sent === 1 ? "1 path" : `${f.requests_sent} paths`} started<Delta info={deltaInfo(data.current.requests_sent, data.previous.requests_sent)} label={before} /></>} />
            <KpiCard icon={UserCheck} label="Acceptance rate" value={pct(data.acceptance_rate)} tone="teal"
              hint={<>{data.acceptance_matured ? `${data.acceptance_matured_accepted} of ${data.acceptance_matured} sent ${data.accept_window_days}+ days ago` : `Nothing sent ${data.accept_window_days}+ days ago yet`}
                <Delta info={deltaInfo(data.current.acceptance_rate, data.previous.acceptance_rate, { unit: "points" })} label={before} /></>} />
            <KpiCard icon={CheckCircle2} label="Reply rate" value={pct(data.reply_rate)} tone="magenta"
              hint={<>{f.replied} {f.replied === 1 ? "reply" : "replies"} ({f.positive} interested or teaming)<Delta info={deltaInfo(data.current.reply_rate, data.previous.reply_rate, { unit: "points" })} label={before} /></>} />
            <KpiCard icon={Clock} label="Time to accept" value={formatHours(data.time_to_accept.median_hours)} tone="info"
              hint={<>{data.time_to_accept.count ? `Median · slowest 10%: ${formatHours(data.time_to_accept.p90_hours)}` : "No acceptance yet"}
                <Delta info={deltaInfo(data.current.median_hours, data.previous.median_hours, { unit: "hours", lowerIsBetter: true })} label={before} /></>} />
            <KpiCard icon={AlertTriangle} label="Dead ends" value={data.dead_ends.total} tone="danger"
              hint={<>Paths that ended without a reply<Delta info={deltaInfo(data.current.dead_ends, data.previous.dead_ends, { lowerIsBetter: true })} label={before} /></>} />
          </div>

          {sent === 0 ? (
            <p className="rounded-xl border border-border bg-surface px-4 py-8 text-center text-sm text-muted-foreground">No connection request was sent in this range.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <section aria-label="Funnel" className="space-y-2 min-w-0">
                <h2 className="text-sm font-semibold text-foreground">Funnel <span className="text-xs font-normal text-muted-foreground">(of the requests sent in the range; later steps may still be ahead)</span></h2>
                <div className="rounded-xl border border-border bg-surface p-4 space-y-2.5">
                  {FUNNEL.map((s) => {
                    const n = f[s.key];
                    return (
                      <div key={s.key} className="grid grid-cols-[10rem_1fr_6.5rem] items-center gap-3 text-sm" data-testid={`funnel-${s.key}`}>
                        <span className="text-foreground truncate">{s.label}</span>
                        <div className="h-3 rounded-full bg-surface-muted overflow-hidden" aria-hidden>
                          <div className={`h-full rounded-full ${s.bar}`} style={{ width: `${Math.max(n > 0 ? 2 : 0, Math.round((n / sent) * 100))}%` }} />
                        </div>
                        <span className="text-right tabular-nums"><strong className="font-semibold">{n}</strong> <span className="text-xs text-muted-foreground">{Math.round((n / sent) * 100)}%</span></span>
                      </div>
                    );
                  })}
                </div>
              </section>
                <ChartCard title="How fast requests get accepted" subtitle={`${data.time_to_accept.count} accepted ${data.time_to_accept.count === 1 ? "request" : "requests"}, by time from sending to acceptance`}
                  table={{ columns: ["Time to accept", "Requests"], rows: data.time_to_accept.buckets.map((b) => [b.label, b.count]) }} testId="chart-accept-time">
                  <ColumnChart ariaLabel="Accepted requests by time to accept" labelValues height={200}
                    categories={data.time_to_accept.buckets.map((b) => SHORT_BUCKET[b.label] ?? b.label)}
                    series={[{ key: "n", label: "Accepted requests", color: SERIES_COLORS[1], values: data.time_to_accept.buckets.map((b) => b.count) }]} />
                </ChartCard>
              </div>
              {weeks.length > 0 && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <ChartCard title="Activity by week sent" subtitle="Requests sent that week, and what became of them"
                    legend={activitySeries} testId="chart-weekly-activity"
                    table={{ columns: ["Week starting", "Requests", "Accepted", "Replied"], rows: data.by_week.map((w) => [shortDay(w.week_start), w.requests_sent, w.accepted, w.replied]) }}>
                    <ColumnChart ariaLabel="Requests sent, accepted and replied, by week" categories={weeks}
                      series={activitySeries.map((s, i) => ({ ...s, values: data.by_week.map((w) => [w.requests_sent, w.accepted, w.replied][i]) }))} />
                  </ChartCard>
                  <ChartCard title="Rates by week sent" subtitle={`Acceptance counts requests sent ${data.accept_window_days}+ days ago only, so recent weeks show no value yet`}
                    legend={rateSeries} testId="chart-weekly-rates"
                    table={{ columns: ["Week starting", "Acceptance rate", "Reply rate"], rows: data.by_week.map((w) => [shortDay(w.week_start), pct(w.acceptance_rate), pct(w.reply_rate)]) }}>
                    <LineChart ariaLabel="Acceptance rate and reply rate by week" categories={weeks}
                      series={[
                        { ...rateSeries[0], values: data.by_week.map((w) => w.acceptance_rate), details: data.by_week.map((w) => w.acceptance_matured ? `${w.acceptance_matured_accepted} of ${w.acceptance_matured}` : undefined) },
                        { ...rateSeries[1], values: data.by_week.map((w) => w.reply_rate), details: data.by_week.map((w) => `${w.replied} of ${w.requests_sent}`) },
                      ]} />
                  </ChartCard>
                </div>
              )}
              {data.send_times.cells.length > 0 && (
                <ChartCard title="Best day and time to send" subtitle="Share of requests accepted, by the weekday and time of day they were sent (only requests that have had their full window)"
                  table={{ columns: ["Day", "Time", "Sent", "Full window", "Accepted", "Rate"], rows: data.send_times.cells.map((c) => [WEEKDAYS[c.weekday], data.send_times.bands[c.band], c.sent, c.matured, c.accepted, c.rate === null ? "—" : `${c.rate}%`]) }}
                  testId="chart-send-times">
                  <SendTimeGrid sendTimes={data.send_times} windowDays={data.accept_window_days} />
                </ChartCard>
              )}
            </>
          )}

          <section aria-label="Replies" className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2 min-w-0">
              <ChartCard title="What replies turned into" subtitle={`${data.replies.started} ${data.replies.started === 1 ? "conversation" : "conversations"} started in the range (first message from the company)`}
                table={{ columns: ["Outcome", "Conversations"], rows: OUTCOME_ROWS.map((o) => [o.label, data.replies.outcomes[o.key]]) }} testId="chart-reply-outcomes">
                {data.replies.started === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">No company wrote back in this range.</p>
                ) : (
                  <div className="space-y-2.5">
                    {OUTCOME_ROWS.map((o) => {
                      const n = data.replies.outcomes[o.key];
                      return (
                        <div key={o.key} className="grid grid-cols-[11rem_1fr_5rem] items-center gap-3 text-sm" data-testid={`outcome-${o.key}`}>
                          <span className="truncate text-foreground">{o.label}</span>
                          <div className="h-3 rounded-full bg-surface-muted overflow-hidden" aria-hidden>
                            <div className="h-full rounded-full" style={{ width: `${Math.max(n > 0 ? 2 : 0, Math.round((n / data.replies.started) * 100))}%`, background: SERIES_COLORS[o.key === "none" ? 0 : 1], opacity: o.key === "none" ? 0.45 : 1 }} />
                          </div>
                          <span className="text-right tabular-nums"><strong className="font-semibold">{n}</strong> <span className="text-xs text-muted-foreground">{Math.round((n / data.replies.started) * 100)}%</span></span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ChartCard>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-3 content-start">
              <KpiCard icon={Clock} label="Our time to answer" value={formatHours(data.replies.response.median_hours)} tone="brand"
                hint={data.replies.response.count ? `Median of ${data.replies.response.count} ${data.replies.response.count === 1 ? "answer" : "answers"} · slowest 10%: ${formatHours(data.replies.response.p90_hours)}` : "We have not answered a reply in this range yet"} />
              <KpiCard icon={MessageCircle} label="Waiting on us right now" value={data.replies.waiting_on_us.count} tone={data.replies.waiting_on_us.count > 0 ? "warning" : "success"}
                hint={data.replies.waiting_on_us.count > 0 ? `Oldest has waited ${data.replies.waiting_on_us.oldest_days} ${data.replies.waiting_on_us.oldest_days === 1 ? "day" : "days"} (any date, not just this range)` : "Every open conversation has been answered"} />
            </div>
          </section>

          <section aria-label="Keeping up" className="space-y-2">
            <h2 className="text-sm font-semibold text-foreground">Keeping up with the daily list <span className="text-xs font-normal text-muted-foreground">(lateness counts working days, Monday to Friday)</span></h2>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <KpiCard icon={CheckCircle2} label="Sent on the due day or before" value={pct(data.keeping_up.on_time.rate)} tone="success"
                hint={data.keeping_up.on_time.sent ? (
                  <>{data.keeping_up.on_time.on_time} of {data.keeping_up.on_time.sent} touches sent in the range
                    {data.keeping_up.on_time.late > 0 && <> · late ones a median {data.keeping_up.on_time.median_days_late} working days late</>}
                    <span className="mt-1 block text-xs">{data.keeping_up.on_time.by_kind.map((k) => `${k.label.split(" ")[0]} ${pct(k.rate)}`).join(" · ")}</span></>
                ) : "No touch with a due day was sent in this range"} />
              <ChartCard title="Overdue right now" subtitle={data.keeping_up.overdue_now.total ? `${data.keeping_up.overdue_now.total} past their due day, oldest ${data.keeping_up.overdue_now.oldest_days} working days (a snapshot, not the range)` : "Nothing is past its due day (a snapshot, not the range)"}
                table={{ columns: ["Late by", "Touches"], rows: data.keeping_up.overdue_now.buckets.map((b) => [b.label, b.count]) }} testId="chart-overdue">
                <BarList rows={data.keeping_up.overdue_now.buckets} total={data.keeping_up.overdue_now.total} color={SERIES_COLORS[2]} testId="overdue" />
              </ChartCard>
              <ChartCard title="Waiting for an acceptance" subtitle={`${data.keeping_up.waiting_now.total} ${data.keeping_up.waiting_now.total === 1 ? "request" : "requests"} out, by how long they have waited (a snapshot; closed after ${data.accept_window_days} days)`}
                table={{ columns: ["Waited", "Requests"], rows: data.keeping_up.waiting_now.buckets.map((b) => [b.label, b.count]) }} testId="chart-waiting">
                <BarList rows={data.keeping_up.waiting_now.buckets} total={data.keeping_up.waiting_now.total} color={SERIES_COLORS[1]} testId="waiting" />
              </ChartCard>
            </div>
          </section>

          <section aria-label="Dead ends" className="space-y-2">
            <h2 className="text-sm font-semibold text-foreground">Dead ends <span className="text-xs font-normal text-muted-foreground">(paths that ended in the range, whenever they began)</span></h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {([["no_profile", "No profile found"], ["closed_unaccepted", "Closed, not accepted"], ["stopped_by_reply", "Stopped by a reply"]] as const).map(([k, label]) => (
                <KpiCard key={k} label={label} value={data.dead_ends[k]} hint={data.dead_ends.total ? `${pct(data.dead_ends.shares[k])} of paths that ended` : "None ended in this range"} />
              ))}
            </div>
          </section>

          <section aria-label="Who says yes" className="space-y-4">
            <h2 className="text-sm font-semibold text-foreground">Who says yes <span className="text-xs font-normal text-muted-foreground">(groups with fewer than {FEW} requests are muted: too few to conclude anything)</span></h2>
            <Breakdown title="By contact role" label="Role" rows={data.by_role.map((r) => ({ ...r, id: r.label, name: r.label }))} />
            <Breakdown title="By company size" label="Head count" rows={data.by_size.map((r) => ({ ...r, id: r.label, name: r.label }))} />
            <Breakdown title="By state of the head office" label="State" rows={foldStates(data.by_state).map((r) => ({ ...r, id: r.label, name: r.label }))} />
          </section>

          <Breakdown title="By RFP" label="RFP" rows={data.by_rfp.map((r) => ({ ...r, id: String(r.opportunity_id), name: r.rfp_title ?? `RFP ${r.opportunity_id}` }))} />
          <Breakdown title="By week sent" label="Week starting" rows={data.by_week.map((r) => ({ ...r, id: r.week_start, name: formatCalendarDate(r.week_start) }))} />
          <Breakdown title="By person who recorded the send" label="Person" rows={data.by_actor.map((r) => ({ ...r, id: r.actor, name: r.label }))} />
        </div>
      )}
    </div>
  );
}
